import type { PlayerIndex, PlayerInputs } from '@md/shared';

/**
 * Clavier partagé : 1 PC, 2 joueurs (concept §5.1).
 *
 * Le mapping utilise `KeyboardEvent.code` (position PHYSIQUE de la touche) et
 * non `key` : `KeyA` est le « Q » d'un AZERTY et le « A » d'un QWERTY, `KeyW`
 * le « Z » d'un AZERTY et le « W » d'un QWERTY. Un seul mapping couvre donc
 * les deux dispositions annoncées dans le concept.
 */
export interface KeyBinding {
  readonly player: PlayerIndex;
  readonly action: keyof PlayerInputs;
}

export const KEY_BINDINGS: Readonly<Record<string, KeyBinding>> = Object.freeze({
  // Polo (joueur 0) — flèches + M. Jamais Ctrl : maintenu pendant que l'autre
  // joueur tape ses lettres, il déclenche les raccourcis du navigateur ou de
  // l'OS (Ctrl+Q quitte, Ctrl+Z annule…). `KeyM` est le M physique d'un QWERTY,
  // `Semicolon` celui d'un AZERTY : les deux dispositions sont couvertes.
  ArrowLeft: { player: 0, action: 'left' },
  ArrowRight: { player: 0, action: 'right' },
  ArrowUp: { player: 0, action: 'jump' },
  KeyM: { player: 0, action: 'action' },
  Semicolon: { player: 0, action: 'action' },
  // Mag (joueur 1) — Q/D (AZERTY) ou A/D (QWERTY), Z/W pour sauter
  KeyA: { player: 1, action: 'left' },
  KeyD: { player: 1, action: 'right' },
  KeyW: { player: 1, action: 'jump' },
  Space: { player: 1, action: 'action' },
  ShiftLeft: { player: 1, action: 'action' },
});

/** Libellés affichés dans l'aide en jeu. */
export const CONTROL_HINTS: readonly { readonly who: string; readonly keys: string }[] = [
  { who: 'Polo', keys: '← → déplacer · ↑ sauter · M champ' },
  { who: 'Mag', keys: 'Q D déplacer · Z sauter · Espace champ' },
];

/**
 * Mode RÉSEAU (phase 3) : un seul robot par appareil, donc les DEUX
 * dispositions pilotent le même personnage. Aucune touche n'est partagée entre
 * deux actions, ce qui évite les surprises en AZERTY comme en QWERTY.
 */
export function netKeyBindings(player: PlayerIndex): Readonly<Record<string, KeyBinding>> {
  return Object.freeze({
    ArrowLeft: { player, action: 'left' },
    KeyA: { player, action: 'left' },
    ArrowRight: { player, action: 'right' },
    KeyD: { player, action: 'right' },
    ArrowUp: { player, action: 'jump' },
    KeyW: { player, action: 'jump' },
    Space: { player, action: 'action' },
    ShiftLeft: { player, action: 'action' },
    KeyM: { player, action: 'action' },
    Semicolon: { player, action: 'action' },
  } satisfies Record<string, KeyBinding>);
}

/** Aide affichée en mode réseau (un robot, un appareil). */
export const NET_CONTROL_HINT = '← → ou A/D déplacer · ↑ ou Z sauter · Espace ou M champ';

export interface KeyboardController {
  /** Branche les écouteurs sur la cible (par défaut `window`). */
  attach(): void;
  /** Débranche et relâche toutes les touches encore enfoncées. */
  detach(): void;
  /** Remplace la table de correspondance (bascule local ↔ réseau). */
  setBindings(bindings: Readonly<Record<string, KeyBinding>>): void;
}

export interface KeyboardOptions {
  /** Appelé quand une touche mappée change d'état. */
  readonly onInput: (player: PlayerIndex, patch: Partial<PlayerInputs>) => void;
  /** Cible des écouteurs — injectable pour les tests unitaires. */
  readonly target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  /** Table de correspondance ; par défaut le clavier partagé (concept §5.1). */
  readonly bindings?: Readonly<Record<string, KeyBinding>>;
}

/** Touches dont le comportement natif (scroll de page) doit être neutralisé. */
const SWALLOWED_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Space']);

export function createKeyboardController(options: KeyboardOptions): KeyboardController {
  const target = options.target ?? window;
  const pressed = new Set<string>();
  let bindings = options.bindings ?? KEY_BINDINGS;

  const apply = (code: string, down: boolean): void => {
    const binding = bindings[code];
    if (!binding) {
      return;
    }
    options.onInput(binding.player, { [binding.action]: down } as Partial<PlayerInputs>);
  };

  const onKeyDown = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    if (SWALLOWED_KEYS.has(keyboardEvent.code)) {
      event.preventDefault();
    }
    if (keyboardEvent.repeat || pressed.has(keyboardEvent.code)) {
      return;
    }
    pressed.add(keyboardEvent.code);
    apply(keyboardEvent.code, true);
  };

  const onKeyUp = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    if (SWALLOWED_KEYS.has(keyboardEvent.code)) {
      event.preventDefault();
    }
    if (!pressed.delete(keyboardEvent.code)) {
      return;
    }
    apply(keyboardEvent.code, false);
  };

  /**
   * Relâche toutes les touches encore enfoncées. `emit = false` se contente
   * d'oublier l'état local : au changement de table (mode local → réseau), le
   * joueur de l'ancienne table n'est plus pilotable par cette session.
   */
  const releaseAll = (emit: boolean): void => {
    if (emit) {
      for (const code of pressed) {
        apply(code, false);
      }
    }
    pressed.clear();
  };

  // Perdre le focus (alt-tab) relâche tout : sinon un robot part en roue libre.
  const onBlur = (): void => releaseAll(true);

  return {
    attach(): void {
      target.addEventListener('keydown', onKeyDown);
      target.addEventListener('keyup', onKeyUp);
      target.addEventListener('blur', onBlur);
    },
    detach(): void {
      onBlur();
      target.removeEventListener('keydown', onKeyDown);
      target.removeEventListener('keyup', onKeyUp);
      target.removeEventListener('blur', onBlur);
    },
    setBindings(next: Readonly<Record<string, KeyBinding>>): void {
      // Oublier les touches enfoncées AVANT de changer de table : sinon un
      // relâchement futur serait attribué au joueur de l'ancienne table.
      releaseAll(false);
      bindings = next;
    },
  };
}
